import {
    AddRounded,
    ArrowForwardRounded,
    CheckCircleRounded,
    ErrorRounded,
    PendingRounded,
    RefreshRounded,
    RouteRounded,
    WarningAmberRounded,
} from "@mui/icons-material";
import {
    Box,
    Chip,
    IconButton,
    Pagination,
    Paper,
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    useTheme,
} from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { KboFlowDebugService } from "services/KboFlowDebugService";
import { UserForSharing, UserService } from "services/UserService";
import OrganizationBreadcrumbs from "../OrganizationBreadcrumbs";
import { formatDuration, shortId } from "../KboFlowDebug/KboFlowDebugUI";
import {
    KboFlowRunSummary,
    KboFlowRunsPage,
} from "../KboFlowDebug/KboFlowRunDebug";
import { ScheduleWorkflowDialog } from "../KboFlowDebug/ScheduleWorkflowDialog";
import { formatRunDate } from "./KboFlowRunDigestModels";

const PAGE_SIZE = 12;
const PAGE_PARAM = "page";

type ProductRunStatus = "completed" | "failed" | "inProgress";

function productRunStatus(run: KboFlowRunSummary): ProductRunStatus {
    switch (run.status?.toLowerCase()) {
        case "completed":
            return "completed";
        case "failed":
            return "failed";
        case "running":
        case "bookmarked":
            return "inProgress";
    }

    const diagnosticStatus = `${run.lastPhaseStatus ?? ""} ${
        run.outcomeMessage ?? ""
    }`.toLowerCase();

    if (run.failed || diagnosticStatus.includes("fail")) return "failed";
    if (
        run.completedAtUtc ||
        diagnosticStatus.includes("completed") ||
        diagnosticStatus.includes("reports=")
    ) {
        return "completed";
    }
    return "inProgress";
}

function RunRow({
    run,
    onOpen,
}: {
    run: KboFlowRunSummary;
    onOpen: (runId: string) => void;
}) {
    const status = productRunStatus(run);
    const hasCompletedNodeFailures =
        status === "completed" && run.failedExplorationNodeCount > 0;
    const duration = formatDuration(run.startedAtUtc, run.completedAtUtc);
    const statusPresentation = {
        completed: {
            color: "success" as const,
            icon: <CheckCircleRounded />,
            label: "Completed",
        },
        failed: {
            color: "error" as const,
            icon: <ErrorRounded />,
            label: "Failed",
        },
        inProgress: {
            color: "default" as const,
            icon: <PendingRounded />,
            label: "In progress",
        },
    }[status];

    return (
        <TableRow
            hover
            tabIndex={0}
            role="link"
            onClick={() => onOpen(run.id)}
            onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onOpen(run.id);
                }
            }}
            sx={{
                cursor: "pointer",
                "&:last-child td": { borderBottom: 0 },
            }}
        >
            <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                    <Box
                        sx={{
                            alignItems: "center",
                            bgcolor:
                                status === "failed"
                                    ? "error.50"
                                    : "primary.50",
                            borderRadius: 2,
                            color:
                                status === "failed"
                                    ? "error.main"
                                    : "primary.main",
                            display: "flex",
                            height: 38,
                            justifyContent: "center",
                            width: 38,
                        }}
                    >
                        <RouteRounded fontSize="small" />
                    </Box>
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {formatRunDate(run.startedAtUtc ?? run.createdOn)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Run ID {shortId(run.runId ?? run.id)}
                        </Typography>
                    </Box>
                </Box>
            </TableCell>
            <TableCell>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                    <Chip
                        size="small"
                        icon={statusPresentation.icon}
                        color={statusPresentation.color}
                        variant="outlined"
                        label={statusPresentation.label}
                        sx={{ fontWeight: 700 }}
                    />
                    {hasCompletedNodeFailures && (
                        <Tooltip title="This run completed despite node-level failures. Its reports use results from completed branches.">
                            <Chip
                                size="small"
                                color="warning"
                                icon={<WarningAmberRounded />}
                                label={`${run.failedExplorationNodeCount} node ${
                                    run.failedExplorationNodeCount === 1
                                        ? "failure"
                                        : "failures"
                                }`}
                                variant="outlined"
                            />
                        </Tooltip>
                    )}
                </Box>
            </TableCell>
            <TableCell>
                <Typography variant="body2">
                    {run.generatedReportCount} report
                    {run.generatedReportCount === 1 ? "" : "s"} generated
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {run.generatedQuestionCount} question
                    {run.generatedQuestionCount === 1 ? "" : "s"} · {" "}
                    {run.analysisFindingCount} finding
                    {run.analysisFindingCount === 1 ? "" : "s"}
                </Typography>
            </TableCell>
            <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
                <Typography variant="body2">
                    {duration === "—" ? "Not available" : duration}
                </Typography>
            </TableCell>
            <TableCell align="right">
                <Tooltip title="Open run">
                    <IconButton
                        size="small"
                        color="primary"
                        aria-label={`Open run from ${formatRunDate(
                            run.startedAtUtc ?? run.createdOn,
                        )}`}
                    >
                        <ArrowForwardRounded />
                    </IconButton>
                </Tooltip>
            </TableCell>
        </TableRow>
    );
}

function TableSkeleton() {
    return (
        <TableBody>
            {Array.from({ length: 6 }).map((_, index) => (
                <TableRow key={index}>
                    {Array.from({ length: 5 }).map((_cell, cellIndex) => (
                        <TableCell key={cellIndex}>
                            <Skeleton height={34} />
                        </TableCell>
                    ))}
                </TableRow>
            ))}
        </TableBody>
    );
}

const KboFlowRuns: React.FC<{ orgId: string }> = ({ orgId }) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const service = useMemo(() => new KboFlowDebugService(), []);
    const userService = useMemo(() => new UserService(), []);
    const [searchParams, setSearchParams] = useSearchParams();
    const [runs, setRuns] = useState<KboFlowRunSummary[]>([]);
    const [users, setUsers] = useState<UserForSharing[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>();
    const [reloadToken, setReloadToken] = useState(0);
    const [scheduleOpen, setScheduleOpen] = useState(false);

    const parsedPage = Number.parseInt(searchParams.get(PAGE_PARAM) ?? "1", 10);
    const page = Number.isNaN(parsedPage) || parsedPage < 1 ? 1 : parsedPage;

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setError(undefined);
        service
            .getRuns(page, PAGE_SIZE, controller.signal)
            .then((result: KboFlowRunsPage) => {
                setRuns(result.runs ?? []);
                setTotalCount(result.totalCount ?? 0);
            })
            .catch((caught: unknown) => {
                if (!controller.signal.aborted) {
                    setError(
                        caught instanceof Error
                            ? caught.message
                            : "KBo Flow runs could not be loaded.",
                    );
                }
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoading(false);
            });
        return () => controller.abort();
    }, [page, reloadToken, service]);

    useEffect(() => {
        if (!orgId) return undefined;
        let cancelled = false;
        userService
            .getUsersForSharing(orgId)
            .then((result) => {
                if (!cancelled) setUsers(result);
            })
            .catch(() => {
                if (!cancelled) setUsers([]);
            });
        return () => {
            cancelled = true;
        };
    }, [orgId, userService]);

    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
    const rangeStart = totalCount === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
    const rangeEnd = Math.min(rangeStart + runs.length - 1, totalCount);

    const setPage = (nextPage: number) => {
        const next = new URLSearchParams(searchParams);
        if (nextPage > 1) next.set(PAGE_PARAM, String(nextPage));
        else next.delete(PAGE_PARAM);
        setSearchParams(next);
    };

    return (
        <Box
            sx={{
                bgcolor: theme.palette.gray[50],
                minHeight: "100%",
                p: { xs: 2, md: 4 },
                width: "100%",
            }}
        >
            <Box sx={{ maxWidth: 1440, mx: "auto" }}>
                <OrganizationBreadcrumbs
                    pageTitle="Flow runs"
                    sectionLabel="KBo"
                />

                <Box
                    sx={{
                        alignItems: { xs: "flex-start", md: "center" },
                        display: "flex",
                        flexDirection: { xs: "column", md: "row" },
                        gap: 2,
                        mb: 3,
                        mt: 2.5,
                    }}
                >
                    <Box>
                        <Typography
                            component="h1"
                            sx={{
                                color: theme.palette.gray[800],
                                fontSize: { xs: 28, md: 34 },
                                fontWeight: 700,
                                letterSpacing: "-0.02em",
                            }}
                        >
                            KBo Flow runs
                        </Typography>
                        <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                            Follow every KBo research run from assembled context
                            to the finding it delivered.
                        </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }} />
                    <AcxButton
                        color="white"
                        variant="outlined"
                        startIcon={<RefreshRounded />}
                        loading={loading}
                        onClick={() => setReloadToken((value) => value + 1)}
                    >
                        Refresh
                    </AcxButton>
                    <AcxButton
                        color="primary"
                        startIcon={<AddRounded />}
                        onClick={() => setScheduleOpen(true)}
                    >
                        Schedule run
                    </AcxButton>
                </Box>

                <ScheduleWorkflowDialog
                    open={scheduleOpen}
                    onClose={() => setScheduleOpen(false)}
                    service={service}
                    users={users}
                    onScheduled={() => setReloadToken((value) => value + 1)}
                />

                <Paper
                    variant="outlined"
                    sx={{
                        borderColor: theme.palette.gray[200],
                        borderRadius: 2.5,
                        overflow: "hidden",
                    }}
                >
                    <Box
                        sx={{
                            alignItems: "center",
                            borderBottom: "1px solid",
                            borderColor: theme.palette.gray[200],
                            display: "flex",
                            px: 2.5,
                            py: 2,
                        }}
                    >
                        <Box>
                            <Typography sx={{ fontWeight: 700 }}>
                                All runs
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                {loading
                                    ? "Loading runs…"
                                    : `Showing ${rangeStart}–${rangeEnd} of ${totalCount}`}
                            </Typography>
                        </Box>
                        <Box sx={{ flex: 1 }} />
                        {!loading && (
                            <Chip
                                size="small"
                                label={`${totalCount} total`}
                                sx={{ fontWeight: 700 }}
                            />
                        )}
                    </Box>

                    {error ? (
                        <Box sx={{ p: 3 }}>
                            <Typography color="error">{error}</Typography>
                        </Box>
                    ) : !loading && totalCount === 0 ? (
                        <Box sx={{ p: 6, textAlign: "center" }}>
                            <RouteRounded
                                sx={{
                                    color: theme.palette.gray[300],
                                    fontSize: 48,
                                }}
                            />
                            <Typography sx={{ fontWeight: 700, mt: 1 }}>
                                No KBo Flow runs yet
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 0.5 }}
                            >
                                Schedule a run to start building the research
                                trail.
                            </Typography>
                        </Box>
                    ) : (
                        <TableContainer>
                            <Table aria-label="KBo Flow runs">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ width: "35%" }}>
                                            Run
                                        </TableCell>
                                        <TableCell sx={{ width: "20%" }}>
                                            Status
                                        </TableCell>
                                        <TableCell sx={{ width: "25%" }}>
                                            Results
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                width: "15%",
                                                display: {
                                                    xs: "none",
                                                    md: "table-cell",
                                                },
                                            }}
                                        >
                                            Duration
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            aria-label="Open"
                                        />
                                    </TableRow>
                                </TableHead>
                                {loading ? (
                                    <TableSkeleton />
                                ) : (
                                    <TableBody>
                                        {runs.map((run) => (
                                            <RunRow
                                                key={run.id}
                                                run={run}
                                                onOpen={(runId) =>
                                                    navigate(
                                                        `/admin/organizations/${orgId}/kbo-flow-runs/${runId}`,
                                                    )
                                                }
                                            />
                                        ))}
                                    </TableBody>
                                )}
                            </Table>
                        </TableContainer>
                    )}
                </Paper>

                {totalPages > 1 && (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            mt: 3,
                        }}
                    >
                        <Pagination
                            count={totalPages}
                            page={Math.min(page, totalPages)}
                            color="primary"
                            disabled={loading}
                            onChange={(_event, value) => setPage(value)}
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default KboFlowRuns;
